import { Logger, LogLevel } from 'next-axiom'
import { sanitizePathnameForTelemetry } from './path'
import { sanitize } from './redaction'

export type SafeNextAxiomLogFields = Record<string, unknown>

export interface SafeNextAxiomLogger {
  debug(message: string, fields?: SafeNextAxiomLogFields): void
  info(message: string, fields?: SafeNextAxiomLogFields): void
  warn(message: string, fields?: SafeNextAxiomLogFields): void
  error(message: string, fields?: SafeNextAxiomLogFields): void
  flush(): Promise<void>
  with(fields: SafeNextAxiomLogFields): SafeNextAxiomLogger
}

type NextAxiomLoggerLike = Pick<
  Logger,
  'debug' | 'info' | 'warn' | 'error' | 'flush' | 'with'
>

export interface SafeNextAxiomLoggerOptions {
  source?: string
}

function isPathLikeTelemetryKey(key: string): boolean {
  const keyLower = key.toLowerCase()
  return (
    keyLower === 'path' ||
    keyLower === 'pathname' ||
    keyLower === 'route' ||
    keyLower === 'url'
  )
}

function normalizePathLikeFields(value: unknown, key = ''): unknown {
  if (typeof value === 'string') {
    return isPathLikeTelemetryKey(key) ? sanitizePathnameForTelemetry(value) : value
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizePathLikeFields(item, key))
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([childKey, childValue]) => [
        childKey,
        normalizePathLikeFields(childValue, childKey),
      ]),
    )
  }

  return value
}

function normalizeTelemetryFields(
  fields: SafeNextAxiomLogFields,
): SafeNextAxiomLogFields {
  return sanitize(
    normalizePathLikeFields(fields) as Record<string, unknown>,
  )
}

function createSafeNextAxiomLoggerFromSanitizedFields(
  logger: NextAxiomLoggerLike,
  sanitizedBaseFields: SafeNextAxiomLogFields,
): SafeNextAxiomLogger {
  const safeFields = (fields?: SafeNextAxiomLogFields) => ({
    ...sanitizedBaseFields,
    ...(fields ? normalizeTelemetryFields(fields) : {}),
  })

  return {
    debug(message, fields) {
      logger.debug(message, safeFields(fields))
    },
    info(message, fields) {
      logger.info(message, safeFields(fields))
    },
    warn(message, fields) {
      logger.warn(message, safeFields(fields))
    },
    error(message, fields) {
      logger.error(message, safeFields(fields))
    },
    flush() {
      return logger.flush()
    },
    with(fields) {
      return createSafeNextAxiomLoggerFromSanitizedFields(logger, safeFields(fields))
    },
  }
}

export function createSafeNextAxiomLoggerFromLogger(
  logger: NextAxiomLoggerLike,
  baseFields: SafeNextAxiomLogFields = {},
): SafeNextAxiomLogger {
  return createSafeNextAxiomLoggerFromSanitizedFields(
    logger,
    normalizeTelemetryFields(baseFields),
  )
}

export function createSafeNextAxiomLogger(
  baseFields: SafeNextAxiomLogFields = {},
  options: SafeNextAxiomLoggerOptions = {},
): SafeNextAxiomLogger {
  const source = options.source ?? 'handled-platform-app'
  const logger = new Logger({
    args: normalizeTelemetryFields({ source }),
    source,
  })
  return createSafeNextAxiomLoggerFromLogger(logger, baseFields)
}

export { LogLevel }
